import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import {
  Container,
  Card,
  CardContent,
  Typography,
  Grid,
  Divider,
  Box,
  CircularProgress,
  Button,
  TextField,
  Select,
  MenuItem,
  Pagination,
  Chip,
  Fab,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import Header1 from "../Header/Header1";

const API_URL = process.env.REACT_APP_API_URL;

/* ------------------------- Stylish Components --------------------------- */

const NeonCard = styled(Card)(() => ({
  background: "rgba(255,255,255,0.05)",
  backdropFilter: "blur(10px)",
  borderRadius: "15px",
  border: "1px solid rgba(255,255,255,0.08)",
  padding: "5px",
  color: "#fff",
  transition: "0.3s ease",
  "&:hover": {
    transform: "translateY(-6px)",
    boxShadow: "0 0 25px rgba(0, 191, 255, 0.45)",
    borderColor: "#00eaff",
  },
}));

const SearchField = styled(TextField)(() => ({
  input: {
    color: "#fff",
  },
  "& .MuiOutlinedInput-root": {
    backgroundColor: "#0f0f0f",
    borderRadius: "8px",
    "& fieldset": { borderColor: "#333" },
    "&:hover fieldset": { borderColor: "#666" },
    "&.Mui-focused fieldset": { borderColor: "#00eaff" },
  },
}));

const FloatingButton = styled(Fab)(() => ({
  position: "fixed",
  bottom: "35px",
  right: "35px",
  background: "linear-gradient(90deg,#00eaff,#ff00ff)",
  color: "#000",
  "&:hover": {
    background: "linear-gradient(90deg,#00c4ff,#ff00cc)",
  },
}));

/* ------------------------------------------------------------------------ */

function AdminFaqs({ userId, onLogout }) {
  const [faqs, setFaqs] = useState([]);
  const [filteredFaqs, setFilteredFaqs] = useState([]);

  const [search, setSearch] = useState("");
  const [rowsPerPage, setRowsPerPage] = useState(6);
  const [currentPage, setCurrentPage] = useState(1);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const searchRef = useRef(null);

  /* -------------------------------------------------------------------- */
  /** Fetch FAQs */
  useEffect(() => {
    const fetchFaqs = async () => {
      setLoading(true);
      try {
        const response = await axios.get(`${API_URL}/get_asked_questions/`);
        setFaqs(response.data);
        setFilteredFaqs(response.data);
      } catch (error) {
        setError("Failed to fetch FAQs");
      } finally {
        setLoading(false);
      }
    };

    fetchFaqs();
  }, []);

  useEffect(() => {
    if (searchRef.current) searchRef.current.focus();
  }, [loading]);

  /* -------------------------------------------------------------------- */
  /** Handle search filtering */
  useEffect(() => {
    const results = faqs.filter((item) =>
      `${item.question} ${item.username}`
        .toLowerCase()
        .includes(search.toLowerCase())
    );
    setFilteredFaqs(results);
    setCurrentPage(1);
  }, [search, faqs]);

  /* -------------------------------------------------------------------- */
  /** Pagination logic */
  const pageCount = Math.ceil(filteredFaqs.length / rowsPerPage);
  const paginatedData = filteredFaqs.slice(
    (currentPage - 1) * rowsPerPage,
    currentPage * rowsPerPage
  );

  /* -------------------------------------------------------------------- */

  return (
    <div>
      <Header1 userId={userId} onLogout={onLogout} />

      <Container
        maxWidth="lg"
        sx={{
          backgroundColor: "#000000ff",
          minHeight: "100vh",
          pt: 10,
          pb: 8,
        }}
      >
        {/* Title */}
        <Typography
          variant="h3"
          align="center"
          sx={{
            color: "#fff",
            fontWeight: 700,
            mb: 4,
            background: "linear-gradient(90deg,#00eaff,#ff00ff)",
            WebkitBackgroundClip: "text",
            color: "transparent",
          }}
        >
          Admin – Asked Questions
        </Typography>

        {/* Search + Controls */}
        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", sm: "row" },
            justifyContent: "space-between",
            alignItems: "center",
            mb: 4,
            gap: 2,
          }}
        >
          <Chip
            label={`Total: ${faqs.length} | Matching: ${filteredFaqs.length}`}
            sx={{
              background: "rgba(0,255,255,0.15)",
              color: "#00eaff",
              fontWeight: "bold",
              px: 2,
              py: 1,
            }}
          />

          <Box display="flex" gap={2} flexWrap="wrap">
            <SearchField
              placeholder="Search question or username..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              inputRef={searchRef}
              size="small"
              sx={{ width: { xs: "100%", sm: "260px" } }}
            />

            <Select
              value={rowsPerPage}
              onChange={(e) => {
                setRowsPerPage(Number(e.target.value));
                setCurrentPage(1);
              }}
              size="small"
              sx={{
                backgroundColor: "#0f0f0f",
                color: "#fff",
                "& .MuiOutlinedInput-notchedOutline": { borderColor: "#333" },
                "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: "#666" },
                "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                  borderColor: "#00eaff",
                },
              }}
            >
              {[6, 12, 18].map((n) => (
                <MenuItem value={n} key={n}>
                  {n}/page
                </MenuItem>
              ))}
            </Select>
          </Box>
        </Box>

        {/* Loading */}
        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", mt: 8 }}>
            <CircularProgress sx={{ color: "#00eaff" }} />
          </Box>
        ) : error ? (
          <Typography sx={{ color: "#ff4747", textAlign: "center" }}>
            {error}
          </Typography>
        ) : filteredFaqs.length === 0 ? (
          <Typography
            variant="h6"
            align="center"
            sx={{ color: "#bbb", mt: 4 }}
          >
            No FAQs found.
          </Typography>
        ) : (
          <>
            {/* FAQ Cards */}
            <Grid container spacing={3}>
              {paginatedData.map((faq) => (
                <Grid item xs={12} sm={6} md={4} key={faq.id}>
                  <NeonCard>
                    <CardContent>
                      <Typography
                        variant="h6"
                        sx={{ color: "#00eaff", fontWeight: "bold", mb: 1 }}
                      >
                        {faq.question}
                      </Typography>

                      <Divider sx={{ backgroundColor: "#555", mb: 1 }} />

                      <Typography
                        sx={{
                          fontStyle: "italic",
                          color: "#ccc",
                          fontSize: "0.9rem",
                        }}
                      >
                        Asked by: {faq.username}
                      </Typography>
                    </CardContent>
                  </NeonCard>
                </Grid>
              ))}
            </Grid>

            {/* Pagination */}
            {pageCount > 1 && (
              <Box sx={{ display: "flex", justifyContent: "center", mt: 5 }}>
                <Pagination
                  count={pageCount}
                  page={currentPage}
                  onChange={(e, val) => setCurrentPage(val)}
                  size="large"
                  sx={{
                    "& .MuiPaginationItem-root": { color: "#00eaff" },
                    "& .Mui-selected": {
                      background:
                        "linear-gradient(90deg,#00eaff,#ff00ff) !important",
                      color: "#000 !important",
                      boxShadow: "0 0 10px #00eaff",
                    },
                  }}
                />
              </Box>
            )}
          </>
        )}

        {/* Floating scroll-to-top btn */}
        <FloatingButton
          onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
        >
          <ArrowUpwardIcon />
        </FloatingButton>
      </Container>
    </div>
  );
}

export default AdminFaqs;
